import { ChangeEvent } from 'react'
import {
  Column,
  Container,
  InputLabel,
  InputToggleSwitch,
  Row,
  Text
} from '@smuchile/pkg-unimarc-components'
import {
  FormField as FormFieldContent,
  FormFieldTypes,
  ValidationsType
} from 'shared/interfaces/Form'
import {
  FormValues,
  sanitizeInputValue,
  validateError,
  withContactabilityLimits
} from '../helpers/helpers'
import classNames from 'classnames'
import styles from '../Form.module.css'

interface FormFieldProps {
  formData: FormValues
  formField: FormFieldContent
  handleInputChange?: (
    sectionId: string,
    fieldId: string,
    value: string | boolean
  ) => void
  sectionId: string
  labelsColor: string
}

export const FormField = ({
  formData,
  formField,
  handleInputChange,
  sectionId,
  labelsColor
}: FormFieldProps) => {
  const effectiveField = withContactabilityLimits(formField)
  const formFieldValue = formData?.[sectionId]?.[formField.formfieldId]

  const onInputChange = (
    e?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    if (!handleInputChange) return
    handleInputChange(
      sectionId,
      formField.formfieldId,
      sanitizeInputValue(e.target.value, effectiveField)
    )
  }
  const onSwitchChange = (value: boolean) => {
    handleInputChange?.(sectionId, formField.formfieldId, value)
  }

  const inputValue = typeof formFieldValue === 'string' ? formFieldValue : ''
  const hasError = !!inputValue && validateError(effectiveField, inputValue)
  const reachedMax =
    (formField.formfieldId === 'custom1' ||
      formField.formfieldId === 'custom2') &&
    !!effectiveField.maxValue &&
    inputValue.length >= effectiveField.maxValue
  const errorMessage = hasError
    ? formField?.errorMessage ?? 'El campo es invalido.'
    : reachedMax
    ? `Alcanzaste el límite máximo de ${effectiveField.maxValue} caracteres.`
    : null

  return (
    <>
      {formField.type === FormFieldTypes.Input && (
        <Column>
          <Row gap={8}>
            {formField.validations === ValidationsType.PhoneNumber && (
              <InputLabel
                customClassName={classNames(styles['input-container'])}
                customInputClassName={classNames(styles['code-input'])}
                disabled={true}
                disabledText={true}
                error={hasError || reachedMax}
                fontSize='lg'
                id='code'
                inputWidth='76px'
                label='Código'
                name='code'
                showXIcon={false}
                value='+569'
                variant='compact'
              />
            )}
            {formField.rows > 1 ? (
              <div className={styles['textarea-container']}>
                <textarea
                  aria-invalid={hasError || reachedMax}
                  className={styles.textarea}
                  id={formField.formfieldId}
                  name={formField.formfieldId}
                  onChange={onInputChange}
                  rows={formField.rows}
                  value={inputValue}
                />
                <label htmlFor={formField.formfieldId}>{formField.label}</label>
              </div>
            ) : (
              <InputLabel
                customClassName={classNames(styles['input-container'])}
                customInputClassName={classNames(styles['input'])}
                error={hasError || reachedMax}
                fontSize='lg'
                fullWidth={true}
                id={formField.formfieldId}
                label={formField.label}
                name={formField.formfieldId}
                onChange={onInputChange}
                showXIcon={false}
                value={inputValue}
                variant='compact'
              />
            )}
          </Row>
          {errorMessage && (
            <Container padding='6px 0 0 16px'>
              <Text
                color='error'
                fontSize='xs'
              >
                {errorMessage}
              </Text>
            </Container>
          )}
        </Column>
      )}
      {formField.type === FormFieldTypes.Switch && (
        <InputToggleSwitch
          handleSelectCheck={onSwitchChange}
          isSelectCheck={Boolean(formFieldValue)}
          label={formField.label}
          labelsColor={labelsColor}
        />
      )}
    </>
  )
}
